import { create } from 'zustand';
import { ticketService } from '@services/ticketService';
import type {
  Ticket,
  TicketCategory,
  TicketNotification,
  NotificationPreference,
  TicketStats,
  TicketLabel,
  PaginatedResponse,
  TicketPriority,
} from '../types/ticket';

interface TicketStore {
  // Data
  tickets: Ticket[];
  currentTicket: Ticket | null;
  categories: TicketCategory[];
  notifications: TicketNotification[];
  unreadCount: number;
  preferences: NotificationPreference | null;
  labels: TicketLabel[];
  stats: TicketStats | null;

  // Pagination
  ticketsPagination: {
    count: number;
    total_pages: number;
    current_page: number;
  } | null;

  // Loading states
  loading: boolean;
  error: string | null;

  // User actions
  fetchTickets: (params?: { status?: string; category?: string; page?: number; page_size?: number }) => Promise<void>;
  createTicket: (data: { title: string; description: string; category?: number; priority?: TicketPriority }) => Promise<Ticket>;
  fetchTicket: (id: string) => Promise<void>;
  sendMessage: (id: string, content: string, files?: File[], isInternal?: boolean) => Promise<void>;
  closeTicket: (id: string) => Promise<void>;
  reopenTicket: (id: string) => Promise<void>;
  rateTicket: (id: string, rating: number, comment?: string) => Promise<void>;
  fetchCategories: () => Promise<void>;

  // Notification actions
  fetchNotifications: (page?: number) => Promise<void>;
  markNotificationRead: (id: number) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  fetchPreferences: () => Promise<void>;
  updatePreferences: (data: Partial<NotificationPreference>) => Promise<void>;

  // Admin actions
  adminFetchTickets: (params?: Record<string, any>) => Promise<void>;
  adminFetchTicket: (id: string) => Promise<void>;
  adminAssignTicket: (id: string, assigneeId: number) => Promise<void>;
  adminChangeStatus: (id: string, ticketStatus: string, note?: string) => Promise<void>;
  adminChangePriority: (id: string, priority: string) => Promise<void>;
  adminUpdateLabels: (id: string, labelIds: number[]) => Promise<void>;
  adminReply: (id: string, content: string, files?: File[], isInternal?: boolean) => Promise<void>;
  adminBulkAction: (data: { ticket_ids: string[]; action: string; assignee_id?: number }) => Promise<number>;
  adminFetchStats: () => Promise<void>;
  adminFetchLabels: () => Promise<void>;

  // Utility
  clearError: () => void;
  clearCurrentTicket: () => void;
}

export const useTicketStore = create<TicketStore>()((set, get) => ({
  tickets: [],
  currentTicket: null,
  categories: [],
  notifications: [],
  unreadCount: 0,
  preferences: null,
  labels: [],
  stats: null,
  ticketsPagination: null,
  loading: false,
  error: null,

  // User actions
  fetchTickets: async (params) => {
    set({ loading: true, error: null });
    try {
      const result = await ticketService.getTickets(params);
      set({
        tickets: result.results,
        ticketsPagination: {
          count: result.count,
          total_pages: result.total_pages,
          current_page: result.current_page,
        },
        loading: false,
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '获取工单列表失败', loading: false });
    }
  },

  createTicket: async (data) => {
    set({ loading: true, error: null });
    try {
      const ticket = await ticketService.createTicket(data);
      set(state => ({
        tickets: [ticket, ...state.tickets],
        loading: false,
      }));
      return ticket;
    } catch (error: any) {
      const msg = error.response?.data?.message || '创建工单失败';
      set({ error: msg, loading: false });
      throw error;
    }
  },

  fetchTicket: async (id) => {
    set({ loading: true, error: null });
    try {
      const ticket = await ticketService.getTicket(id);
      set({ currentTicket: ticket, loading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '获取工单详情失败', loading: false });
    }
  },

  sendMessage: async (id, content, files, isInternal) => {
    set({ loading: true, error: null });
    try {
      await ticketService.sendMessage(id, { content, is_internal: isInternal }, files);
      const ticket = await ticketService.getTicket(id);
      set({ currentTicket: ticket, loading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '发送消息失败', loading: false });
    }
  },

  closeTicket: async (id) => {
    set({ loading: true, error: null });
    try {
      await ticketService.closeTicket(id);
      const ticket = await ticketService.getTicket(id);
      set({ currentTicket: ticket, loading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '关闭工单失败', loading: false });
    }
  },

  reopenTicket: async (id) => {
    set({ loading: true, error: null });
    try {
      await ticketService.reopenTicket(id);
      const ticket = await ticketService.getTicket(id);
      set({ currentTicket: ticket, loading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '重开工单失败', loading: false });
    }
  },

  rateTicket: async (id, rating, comment) => {
    set({ loading: true, error: null });
    try {
      await ticketService.rateTicket(id, rating, comment);
      const ticket = await ticketService.getTicket(id);
      set({ currentTicket: ticket, loading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '评价失败', loading: false });
    }
  },

  fetchCategories: async () => {
    try {
      const categories = await ticketService.getCategories();
      set({ categories });
    } catch (error: any) {
      console.error('Failed to fetch categories:', error);
    }
  },

  // Notification actions
  fetchNotifications: async (page) => {
    try {
      const result = await ticketService.getNotifications({ page });
      set({ notifications: result.results });
    } catch (error: any) {
      console.error('Failed to fetch notifications:', error);
    }
  },

  markNotificationRead: async (id) => {
    try {
      await ticketService.markNotificationRead(id);
      set(state => ({
        notifications: state.notifications.map(n =>
          n.id === id ? { ...n, is_read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch (error: any) {
      console.error('Failed to mark notification read:', error);
    }
  },

  markAllNotificationsRead: async () => {
    try {
      await ticketService.markAllNotificationsRead();
      set(state => ({
        notifications: state.notifications.map(n => ({ ...n, is_read: true })),
        unreadCount: 0,
      }));
    } catch (error: any) {
      console.error('Failed to mark all read:', error);
    }
  },

  fetchUnreadCount: async () => {
    try {
      const count = await ticketService.getUnreadCount();
      set({ unreadCount: count });
    } catch (error: any) {
      console.error('Failed to fetch unread count:', error);
    }
  },

  fetchPreferences: async () => {
    try {
      const preferences = await ticketService.getNotificationPreferences();
      set({ preferences });
    } catch (error: any) {
      console.error('Failed to fetch preferences:', error);
    }
  },

  updatePreferences: async (data) => {
    try {
      const preferences = await ticketService.updateNotificationPreferences(data);
      set({ preferences });
    } catch (error: any) {
      console.error('Failed to update preferences:', error);
    }
  },

  // Admin actions
  adminFetchTickets: async (params) => {
    set({ loading: true, error: null });
    try {
      const result = await ticketService.adminGetTickets(params);
      set({
        tickets: result.results,
        ticketsPagination: {
          count: result.count,
          total_pages: result.total_pages,
          current_page: result.current_page,
        },
        loading: false,
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '获取工单列表失败', loading: false });
    }
  },

  adminFetchTicket: async (id) => {
    set({ loading: true, error: null });
    try {
      const ticket = await ticketService.adminGetTicket(id);
      set({ currentTicket: ticket, loading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '获取工单详情失败', loading: false });
    }
  },

  adminAssignTicket: async (id, assigneeId) => {
    try {
      const ticket = await ticketService.adminAssignTicket(id, assigneeId);
      set({ currentTicket: ticket });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '分配工单失败' });
    }
  },

  adminChangeStatus: async (id, ticketStatus, note) => {
    try {
      const ticket = await ticketService.adminChangeStatus(id, ticketStatus, note);
      set({ currentTicket: ticket });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '状态变更失败' });
    }
  },

  adminChangePriority: async (id, priority) => {
    try {
      const ticket = await ticketService.adminChangePriority(id, priority);
      set({ currentTicket: ticket });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '优先级变更失败' });
    }
  },

  adminUpdateLabels: async (id, labelIds) => {
    try {
      const ticket = await ticketService.adminUpdateLabels(id, labelIds);
      set({ currentTicket: ticket });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '标签更新失败' });
    }
  },

  adminReply: async (id, content, files, isInternal) => {
    try {
      await ticketService.adminReply(id, { content, is_internal: isInternal }, files);
      const ticket = await ticketService.adminGetTicket(id);
      set({ currentTicket: ticket });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '回复失败' });
    }
  },

  adminBulkAction: async (data) => {
    try {
      const result = await ticketService.adminBulkAction(data as any);
      return result.count;
    } catch (error: any) {
      set({ error: error.response?.data?.message || '批量操作失败' });
      return 0;
    }
  },

  adminFetchStats: async () => {
    try {
      const stats = await ticketService.adminGetStats();
      set({ stats });
    } catch (error: any) {
      console.error('Failed to fetch stats:', error);
    }
  },

  adminFetchLabels: async () => {
    try {
      const labels = await ticketService.adminGetLabels();
      set({ labels });
    } catch (error: any) {
      console.error('Failed to fetch labels:', error);
    }
  },

  // Utility
  clearError: () => set({ error: null }),
  clearCurrentTicket: () => set({ currentTicket: null }),
}));
